import { useState, useEffect, useRef, useCallback } from 'react';
import { Printer, ChevronLeft, FileText, CheckCircle, XCircle } from 'lucide-react';
import { smartBack } from './lib/navBack';
import { addressLines } from './addressLines';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TECH_BACKEND_URL = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/estimates` : 'http://localhost:8002';
const LOGO_URL = 'https://portal.aaairrigationservice.com/header_new.png';
const SEAL_URL = 'https://portal.aaairrigationservice.com/seal.png';
// Signature images are ERPNext File attachments (custom_signature_image stores a
// root-relative /files/... path) — they're served by ERP itself, not by this
// portal's own backend, which has no /api/files proxy route. The printed
// Estimate PDF ("Estimate" print format) builds the same URL against the ERP
// host; mirror that here so the portal view doesn't 404 on every signature.
const ERP_HOST = 'https://erp.aaairrigationservice.com';

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

export default function EstimateView({ customerId, quotationId, onClose, onActioned }) {
  const [doc, setDoc]             = useState(null);
  const [loadErr, setLoadErr]     = useState('');
  const [busy, setBusy]           = useState('');
  const [result, setResult]       = useState(null);
  const [hasSigned, setHasSigned] = useState(false);
  const [sigError, setSigError]   = useState('');
  const [photos, setPhotos]       = useState([]);
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const printRef  = useRef(null);

  useEffect(() => {
    if (!customerId || !quotationId) return;
    fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}`)
      .then(r => r.json())
      .then(d => d.data ? setDoc(d.data) : setLoadErr('Could not load estimate.'))
      .catch(() => setLoadErr('Could not load estimate.'));

    fetch(`${TECH_BACKEND_URL}/api/photos/Quotation/${quotationId}`)
      .then(r => r.json())
      .then(d => setPhotos(d.photos || []))
      .catch(() => setPhotos([]));

    // Portal activity tracking (2026-08-07) — fire-and-forget, must never
    // block rendering or surface an error to the customer.
    fetch(`${BACKEND_URL}/api/track/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_type: 'Quotation', doc_name: quotationId }),
    }).catch(() => {});
  }, [customerId, quotationId]);

  useEffect(() => {
    if (!doc) return;

    const handleBeforePrint = () => {
      if (!printRef.current) return;
      if (document.getElementById('estimate-print-wrapper')) return;

      const wrapper = document.createElement('div');
      wrapper.id = 'estimate-print-wrapper';
      wrapper.innerHTML = printRef.current.outerHTML;
      document.body.appendChild(wrapper);
      document.body.classList.add('printing-estimate');
    };

    const handleAfterPrint = () => {
      const wrapper = document.getElementById('estimate-print-wrapper');
      if (wrapper) {
        wrapper.remove();
      }
      document.body.classList.remove('printing-estimate');
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
      const wrapper = document.getElementById('estimate-print-wrapper');
      if (wrapper) {
        wrapper.remove();
      }
      document.body.classList.remove('printing-estimate');
    };
  }, [doc]);

  const canAct = doc && doc.docstatus !== 2 &&
    ['Draft', 'Pending Approval', 'Sent for Approval'].includes(doc.custom_approval_status);

  const initCanvas = useCallback((el) => {
    if (!el) return;
    canvasRef.current = el;
    const ctx = el.getContext('2d');
    const pos = (e) => {
      const r = el.getBoundingClientRect();
      const s = e.touches ? e.touches[0] : e;
      return { x: (s.clientX - r.left) * (el.width / r.width), y: (s.clientY - r.top) * (el.height / r.height) };
    };
    el.addEventListener('mousedown',  e => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); });
    el.addEventListener('mousemove',  e => { if (!drawing.current) return; ctx.strokeStyle = '#0a2463'; ctx.lineWidth = 2; ctx.lineCap = 'round'; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasSigned(true); });
    el.addEventListener('mouseup',    () => { drawing.current = false; });
    el.addEventListener('mouseleave', () => { drawing.current = false; });
    el.addEventListener('touchstart', e => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }, { passive: false });
    el.addEventListener('touchmove',  e => { e.preventDefault(); if (!drawing.current) return; ctx.strokeStyle = '#0a2463'; ctx.lineWidth = 2; ctx.lineCap = 'round'; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasSigned(true); }, { passive: false });
    el.addEventListener('touchend',   () => { drawing.current = false; });
  }, []);

  const clearSig = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasSigned(false);
  };

  const handleApprove = useCallback(async () => {
    setSigError('');
    if (!hasSigned) { setSigError('Please sign before approving.'); return; }
    setBusy('approving');
    try {
      const sig = canvasRef.current.toDataURL('image/png');
      const sr = await fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}/signature`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: sig }),
      });
      if (!sr.ok) throw new Error();
      const ar = await fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const d = await ar.json();
      if (d.success) {
        setResult({ ok: true, msg: 'Estimate approved! We will be in touch shortly.' });
        // Re-fetch document to get the updated status and signature
        const r = await fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}`);
        const fresh = await r.json();
        if (fresh.data) setDoc(fresh.data);
        if (onActioned) onActioned();
      }
      else setResult({ ok: false, msg: d.message || 'Something went wrong.' });
    } catch { setResult({ ok: false, msg: 'Network error. Please try again.' }); }
    finally { setBusy(''); }
  }, [customerId, quotationId, hasSigned, onActioned]);

  const handleDecline = useCallback(async () => {
    setBusy('declining');
    try {
      const r = await fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}/decline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const d = await r.json();
      if (d.success) {
        setResult({ ok: true, msg: 'Estimate declined.' });
        // Re-fetch document to get updated status
        const fr = await fetch(`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}`);
        const fresh = await fr.json();
        if (fresh.data) setDoc(fresh.data);
        if (onActioned) onActioned();
      }
      else setResult({ ok: false, msg: d.message || 'Something went wrong.' });
    } catch { setResult({ ok: false, msg: 'Network error. Please try again.' }); }
    finally { setBusy(''); }
  }, [customerId, quotationId, onActioned]);

  const items = doc?.items || [];
  const subtotal  = parseFloat(doc?.net_total || 0);
  const tax       = parseFloat(doc?.total_taxes_and_charges || 0);
  const discount  = parseFloat(doc?.discount_amount || 0);
  const grandTotal = parseFloat(doc?.grand_total || 0);
  const approved  = doc?.custom_approval_status === 'Approved';

  // "Back" here is called `onClose` for historical reasons (this view was
  // originally always a same-tab in-app navigation), but it's now most often
  // opened via window.open() in a new tab (Dashboard estimate links) which
  // has no browser history to return to — see lib/navBack.js. Always fall
  // through to smartBack() so the button never goes dead, whether or not a
  // caller supplied onClose.
  const handleBack = () => {
    if (onClose) { onClose(); return; }
    smartBack(customerId ? `/dashboard?customer=${encodeURIComponent(customerId)}` : '/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          body.printing-estimate > *:not(#estimate-print-wrapper) {
            display: none !important;
          }
          #estimate-print-wrapper {
            display: block !important;
            background: white !important;
          }
          #estimate-print-wrapper .print-container {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 20px !important;
          }
        }
      `}</style>

      {/* Action bar */}
      <div className="no-print sticky top-0 bg-white shadow-md z-40 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-[#0a2463] transition-colors">
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <a
              href={`${BACKEND_URL}/api/customer/${customerId}/estimate/${quotationId}/pdf?session=${encodeURIComponent(localStorage.getItem('portal_token') || '')}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors no-underline"
            >
              <FileText className="w-4 h-4" />
              PDF
            </a>
          </div>
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div className={`no-print px-6 py-3 text-sm font-semibold flex items-center justify-between ${result.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <span>{result.msg}</span>
          {result.ok && <button onClick={handleBack} className="underline font-bold">Close</button>}
        </div>
      )}

      <div className="py-8 px-4">
        {loadErr && <div className="text-center text-red-500 py-20">{loadErr}</div>}

        {!doc && !loadErr && (
          <div className="text-center text-gray-400 py-20">
            <div className="w-8 h-8 border-4 border-[#1b7abf] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            Loading estimate…
          </div>
        )}

        {doc && (
          <div ref={printRef} className="print-container max-w-4xl mx-auto shadow-xl rounded-lg overflow-hidden relative">

            {/* First page cover-up for fixed header */}
            <div className="print-first-page-cover" />

            {/* Continuation Header */}
            <div className="continuation-header">
              <span>AAA IRRIGATION SERVICE — ESTIMATE #{doc.name} (Continuation)</span>
              <span>Page <span className="page-number-continuation" /></span>
            </div>

            {/* Background layer */}
            <div className="absolute inset-0 bg-white" style={{ zIndex: -20 }} />

            {/* Seal watermark — multiply blend: renders above content but passes through white, seal stays visible without blocking text */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none seal-watermark" style={{ opacity: 0.12, zIndex: 20, mixBlendMode: 'multiply' }}>
              <img src={SEAL_URL} alt="" className="object-contain" style={{ width: 520, height: 520 }} />
            </div>

            {/* Approved stamp */}
            {approved && (
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none rotate-[-15deg]">
                <div className="border-8 border-green-600 rounded-lg px-8 py-4" style={{ opacity: 0.65 }}>
                  <p className="text-6xl font-black text-green-600 tracking-widest">APPROVED</p>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="relative z-10 p-8">

              {/* Header */}
              <div className="flex items-start justify-between border-b-4 border-[#1b7abf] pb-6 mb-6">
                <div className="text-left">
                  <p className="font-bold text-[#1b7abf] text-lg">AAA IRRIGATION SERVICE, LLC.</p>
                  <p className="text-gray-600">14 Monroe Ct.</p>
                  <p className="text-gray-600">Allen TX 75002</p>
                  <p className="text-gray-600">469-751-3567</p>
                  <p className="text-[#1b7abf]">www.aaairrigationservice.com</p>
                </div>
                <div className="flex-1 flex justify-center">
                  <img src={LOGO_URL} alt="AAA Irrigation Service" className="h-28 w-auto" />
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-bold text-[#1b7abf] tracking-wider">ESTIMATE</h2>
                </div>
              </div>

              {/* Two-column info */}
              <div className="flex justify-between mb-8">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Bill To</p>
                  <p className="text-xl font-bold text-[#1b7abf]">{doc.customer_name}</p>
                  {doc.custom_billing_address && <p className="text-gray-600 whitespace-pre-line">{addressLines(doc.custom_billing_address)}</p>}
                  {doc.custom_service_address && doc.custom_service_address !== doc.custom_billing_address && (
                    <div className="mt-3 no-print">
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Service Address</p>
                      <p className="text-gray-600 whitespace-pre-line">{addressLines(doc.custom_service_address)}</p>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <table className="ml-auto text-sm">
                    <tbody>
                      <tr><td className="text-right text-gray-500 pr-4 pb-1">Estimate #:</td><td className="text-left font-bold text-[#1b7abf]">{doc.name}</td></tr>
                      <tr><td className="text-right text-gray-500 pr-4 pb-1">Date:</td><td className="text-left">{doc.transaction_date}</td></tr>
                      {doc.custom_scheduled_date && <tr><td className="text-right text-gray-500 pr-4 pb-1">Scheduled:</td><td className="text-left">{doc.custom_scheduled_date}</td></tr>}
                      {doc.custom_technician_name && <tr><td className="text-right text-gray-500 pr-4">Tech:</td><td className="text-left">{doc.custom_technician_name}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Line items */}
              <div className="mb-8">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#1b7abf] text-white">
                      <th className="py-3 px-4 text-left font-semibold">Zone #</th>
                      <th className="py-3 px-4 text-left font-semibold">Description</th>
                      <th className="py-3 px-4 text-center font-semibold">Qty</th>
                      <th className="py-3 px-4 text-right font-semibold">Rate</th>
                      <th className="py-3 px-4 text-right font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => {
                      if (item.custom_is_subtotal || item.item_code === 'SUBTOTAL') {
                        return (
                          <tr key={i} className="bg-blue-50/40 font-semibold text-[#1b7abf]">
                            <td colSpan={4} className="py-3 px-4 text-right border-b border-blue-200 italic">
                              {(item.item_name && item.item_name.toLowerCase() !== 'subtotal') ? item.item_name : 'Section Subtotal'}
                            </td>
                            <td className="py-3 px-4 text-right border-b border-blue-200">
                              {fmt(item._section_total || 0)}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-gray-50/40' : 'bg-transparent'}>
                          <td className="py-3 px-4 border-b border-gray-200">{item.custom_zone || ''}</td>
                          <td className="py-3 px-4 border-b border-gray-200">
                            <div>{(item.description || item.item_name || '').replace(/<[^>]+>/g, '').trim()}</div>
                            {(item.custom_before_photo_url || item.custom_after_photo_url) && (
                              <div className="mt-2 flex gap-2 no-print">
                                {item.custom_before_photo_url && (
                                  <div className="flex flex-col items-center">
                                    <span className="text-[10px] text-gray-400 font-medium">Before</span>
                                    <a href={item.custom_before_photo_url} target="_blank" rel="noreferrer">
                                      <img
                                        src={item.custom_before_photo_url}
                                        alt="Before service"
                                        className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity"
                                      />
                                    </a>
                                  </div>
                                )}
                                {item.custom_after_photo_url && (
                                  <div className="flex flex-col items-center">
                                    <span className="text-[10px] text-gray-400 font-medium">After</span>
                                    <a href={item.custom_after_photo_url} target="_blank" rel="noreferrer">
                                      <img
                                        src={item.custom_after_photo_url}
                                        alt="After service"
                                        className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity"
                                      />
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 border-b border-gray-200 text-center">{item.qty}</td>
                          <td className="py-3 px-4 border-b border-gray-200 text-right">{fmt(item.rate)}</td>
                          <td className="py-3 px-4 border-b border-gray-200 text-right font-medium">{fmt(item.amount)}</td>
                        </tr>
                      );
                    })}
                    {Array.from({ length: Math.max(0, 5 - items.length) }).map((_, i) => (
                      <tr key={`pad${i}`} className={(items.length + i) % 2 === 0 ? 'bg-gray-50/40' : 'bg-transparent'}>
                        {[...Array(5)].map((__, j) => <td key={j} className="py-3 px-4 border-b border-gray-200">&nbsp;</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Job Notes (custom_service_description) intentionally NOT rendered here —
                  it's an internal/raw notes field (call-taker/phone-agent freeform text)
                  that has repeatedly leaked non-customer-safe content onto customer-facing
                  documents. custom_ai_summary below is the vetted customer-facing
                  description. See scheduler-memory/estimate_job_notes_customer_leak_fix_2026_07_24.md. */}

              {/* AI-generated summary */}
              {doc.custom_ai_summary && (
                <div className="mb-8 border-t border-gray-200 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Summary</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{doc.custom_ai_summary}</p>
                </div>
              )}

              {/* Job photos, grouped by line item */}
              {photos.length > 0 && (
                <div className="no-print mb-8 border-t border-gray-200 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Job Photos</p>
                  <div className="space-y-4">
                    {Array.from(new Set(photos.map(p => p.line_idx).filter(Boolean))).sort((a, b) => a - b).map(lineIdx => {
                      const linePhotos = photos.filter(p => p.line_idx === lineIdx);
                      const lineItem = items[lineIdx - 1];
                      const before = linePhotos.find(p => p.photo_type === 'before');
                      const after = linePhotos.find(p => p.photo_type === 'after');
                      return (
                        <div key={lineIdx}>
                          {lineItem && (
                            <p className="text-xs text-gray-500 mb-1">
                              <strong>Line {lineIdx}:</strong> {(lineItem.description || lineItem.item_name || '').replace(/<[^>]+>/g, '').trim()}
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-3 max-w-md">
                            {[['Before', before], ['After', after]].map(([label, p]) => (
                              <div key={label}>
                                <p className="text-xs font-medium text-gray-400 mb-1">{label}</p>
                                {p ? (
                                  <img
                                    src={`${TECH_BACKEND_URL}/api/photos/${p.id}/view`}
                                    alt={`${label} — line ${lineIdx}`}
                                    className="w-full h-32 object-cover rounded-md border border-gray-200"
                                  />
                                ) : (
                                  <div className="w-full h-32 rounded-md border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-300">
                                    No photo
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="flex justify-end mb-8">
                <div className="w-72">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">{fmt(subtotal)}</span>
                  </div>
                  {tax > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Tax (8.25%):</span>
                      <span className="font-medium">{fmt(tax)}</span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-red-500">-{fmt(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 bg-[#1b7abf] text-white px-4 rounded-lg mt-2">
                    <span className="font-bold text-lg">TOTAL:</span>
                    <span className="font-bold text-lg">{fmt(grandTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Signature section */}
              {(!canAct || result?.ok) && doc && doc.custom_approval_status === 'Approved' && (
                <div className="border-t-2 border-gray-200 pt-6 mb-6">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Notes</p>
                  <p className="text-xs text-gray-600 leading-relaxed mb-4"><strong>Payment is due upon completion unless different terms are printed above.</strong> Pricing is subject to change if unforeseen issues are discovered. Restored water pressure may reveal additional issues not detectable under low-pressure conditions. Any new problems will be reviewed with the customer before proceeding.</p>
                  <div className="flex gap-8 items-end flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Customer Signature</p>
                      {doc.custom_signature_image ? (
                        <div className="w-full border border-dashed border-gray-300 rounded-lg p-2 bg-gray-50 flex items-center justify-center" style={{ height: 80 }}>
                          <img
                            src={doc.custom_signature_image.startsWith('http') ? doc.custom_signature_image : `${ERP_HOST}${doc.custom_signature_image}`}
                            alt="Customer Signature"
                            className="max-h-[64px] object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-full border border-dashed border-gray-300 rounded-lg p-2 bg-gray-50 flex items-center justify-center text-xs text-gray-400 italic" style={{ height: 80 }}>
                          {doc.custom_signature_captured
                            ? 'Signature recorded (image unavailable)'
                            : 'Approved by phone/in-person — no digital signature on file'}
                        </div>
                      )}
                    </div>
                    <div className="min-w-[140px] max-w-[200px]">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Printed Name</p>
                      <div className="border-b border-gray-800 pb-1 text-sm truncate">{doc.customer_name}</div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Date Signed</p>
                      <div className="border-b border-gray-800 pb-1 text-sm">
                        {doc.modified ? new Date(doc.modified.replace(' ', 'T')).toLocaleDateString() : new Date().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Signature section */}
              {canAct && !result?.ok && (
                <div className="no-print border-t-2 border-gray-200 pt-6 mb-6">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Notes</p>
                  <p className="text-xs text-gray-600 leading-relaxed mb-4"><strong>Payment is due upon completion unless different terms are printed above.</strong> Pricing is subject to change if unforeseen issues are discovered. Restored water pressure may reveal additional issues not detectable under low-pressure conditions. Any new problems will be reviewed with the customer before proceeding.</p>
                  <p className="text-sm text-gray-500 mb-4">By signing below, you authorize AAA Irrigation Service to perform the work described above.</p>
                  <div className="flex gap-8 items-end flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Customer Signature</p>
                      <canvas
                        ref={initCanvas}
                        width={600} height={80}
                        className="w-full border border-dashed border-gray-400 rounded-lg bg-gray-50 cursor-crosshair touch-none"
                        style={{ height: 80 }}
                      />
                      {hasSigned && <button onClick={clearSig} className="mt-1 text-xs text-gray-400 hover:text-gray-600">Clear</button>}
                    </div>
                    <div className="min-w-[140px] max-w-[200px]">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Printed Name</p>
                      <div className="border-b border-gray-800 pb-1 text-sm truncate">{doc.customer_name}</div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Date</p>
                      <div className="border-b border-gray-800 pb-1 text-sm">{new Date().toLocaleDateString()}</div>
                    </div>
                  </div>
                  {sigError && <p className="text-red-500 text-xs mt-2 font-semibold">{sigError}</p>}

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleApprove}
                      disabled={!!busy}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1b7abf] text-white rounded-xl font-bold text-base hover:bg-[#155f96] transition-all disabled:opacity-60"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {busy === 'approving' ? 'Approving…' : 'Approve & Sign'}
                    </button>
                    <button
                      onClick={handleDecline}
                      disabled={!!busy}
                      className="px-6 py-3 border-2 border-red-400 text-red-500 rounded-xl font-semibold hover:bg-red-50 transition-all disabled:opacity-60"
                    >
                      <XCircle className="w-5 h-5 inline mr-1" />
                      {busy === 'declining' ? '…' : 'Decline'}
                    </button>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="text-center pt-4 border-t border-gray-300 mt-4 print-footer">
                <p className="text-xs text-gray-500 italic">
                  Irrigation in Texas is regulated by the TCEQ, P.O. Box 13087, Austin, Texas 78711-3087
                </p>
                <p className="text-xs mt-1 font-semibold" style={{ color: '#00cc44' }}>Powered by Boatman Systems™</p>
                <div className="print-only-page-number text-xs text-gray-400 mt-1" />
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
